import { Button } from 'primereact/button';
import { ChangeEvent, ComponentType, FormEvent, FunctionComponent, memo, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { useLocation } from 'react-router-dom';
import { Editor, EditorTextChangeEvent } from 'primereact/editor';
import Quill from 'quill';
import MagicUrl from 'quill-magic-url';

Quill.register('modules/autoLink', MagicUrl);

export enum UserType {
    clinician,
    patient,
}
export interface Episode {
    id: string;
    patientId: string | null;
    clinicianId: string | null;
}
export interface MessageSenderProps {
    isNewConversation: boolean;
    userType: UserType;
    handleMessageSubmission: (formData: MessageForm) => void;
    cancelMessageSubmission?: () => void;
    isFormVisible: boolean;
    disabled: boolean;
    formValue?: MessageForm | null;
    EpisodeSelector?: ComponentType<any>;
}
export interface MessageForm {
    message: string;
    topic: string | undefined;
    clinicalNotes: string;
    episode: Episode | null;
    minutes: string | undefined;
    isValid: boolean;
}

export interface ConversationContext {
    episodeId: string;
    clinicianId: string;
    clinicianName?: string;
}

export const MessageSender: FunctionComponent<MessageSenderProps> = ({
    isNewConversation,
    userType,
    handleMessageSubmission,
    cancelMessageSubmission,
    isFormVisible,
    disabled,
    formValue,
    EpisodeSelector,
}) => {
    const location = useLocation<{
        conversationContext: ConversationContext | null | undefined;
    }>();
    const conversationContext = location.state?.conversationContext as ConversationContext | null;
    const [form, setForm] = useState<MessageForm>(() => {
        return (
            formValue ??
            ({
                episode: {
                    id: conversationContext?.episodeId,
                    clinicianId: conversationContext?.clinicianId,
                },
            } as MessageForm)
        );
    });
    const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
    const [showForm, setShowForm] = useState<boolean>((isFormVisible && !disabled) || !!formValue);
    const modules = {
        autoLink: true,
    };

    const validateForm = (): boolean => {
        if (!form.message) return false;

        const clinicianBaseValidation: boolean = !form.minutes || !form.clinicalNotes;
        const newConversationBaseValidation: boolean = !form.topic || !form.episode;
        const isClinicianUser = userType === UserType.clinician;

        if (!isNewConversation && isClinicianUser && clinicianBaseValidation) return false;
        if (isNewConversation && isClinicianUser && clinicianBaseValidation && newConversationBaseValidation)
            return false;
        if (isNewConversation && !isClinicianUser && newConversationBaseValidation) return false;

        return true;
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsFormSubmitted(true);
        const validatedForm = { ...form, isValid: validateForm() };
        handleMessageSubmission(validatedForm);
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };
    const handleEditorChange = (e: EditorTextChangeEvent, fieldName: string) => {
        const { htmlValue } = e;
        setForm({ ...form, [fieldName]: htmlValue ?? '' });
    };
    const renderEditorHeader = () => {
        return (
            <span className="ql-formats">
                <Button className="ql-link p-button-link" aria-label="Link" />
            </span>
        );
    };
    const renderForm = (): JSX.Element[] => {
        const formData: JSX.Element[] = [];
        if (isNewConversation) {
            formData.push(
                <>
                    {!!EpisodeSelector && userType === UserType.clinician && (
                        <div className="p-inputgroup flex-1">
                            <EpisodeSelector
                                onSelectedEpisode={(e) => setForm({ ...form, episode: e })}
                                selectedEpisodeParam={form?.episode}
                            />
                        </div>
                    )}

                    {userType === UserType.patient && (
                        <div className="p-inputgroup flex-1">
                            <InputText
                                value={conversationContext?.clinicianName}
                                placeholder="Owner"
                                name="owner"
                                readOnly
                                disabled
                            />
                        </div>
                    )}

                    <div className="p-inputgroup flex-1">
                        <InputText
                            value={form?.topic}
                            placeholder="Topic"
                            name="topic"
                            onChange={handleInputChange}
                            className={classNames({
                                'p-invalid': isFormSubmitted && (!form.topic || form.topic.trim() === ''),
                            })}
                        />
                    </div>
                </>,
            );
        }
        formData.push(
            <>
                <div className="p-inputgroup flex-1">
                    <Editor
                        value={form.message}
                        name="message"
                        placeholder="Message"
                        aria-label="message"
                        data-testid="message"
                        headerTemplate={renderEditorHeader()}
                        onTextChange={(e: EditorTextChangeEvent) => handleEditorChange(e, 'message')}
                        modules={modules}
                        className={classNames({
                            'p-invalid': isFormSubmitted && (!form.message || form.message.trim() === ''),
                        })}
                    />
                </div>
                {userType === UserType.clinician && (
                    <div className="p-inputgroup flex-1">
                        <Editor
                            value={form.clinicalNotes}
                            name="clinicalNotes"
                            aria-label="clinicalNotes"
                            placeholder="Clinical Notes"
                            headerTemplate={renderEditorHeader()}
                            onTextChange={(e: EditorTextChangeEvent) => handleEditorChange(e, 'clinicalNotes')}
                            modules={modules}
                            data-testid="clinicalNotes"
                            className={classNames({
                                'p-invalid':
                                    isFormSubmitted && (!form.clinicalNotes || form.clinicalNotes.trim() === ''),
                            })}
                        />
                    </div>
                )}

                <div className="p-inputgroup flex-1 message-send-buton">
                    {userType === UserType.clinician && (
                        <InputText
                            onChange={handleInputChange}
                            value={form.minutes}
                            placeholder="Minutes"
                            type="number"
                            name="minutes"
                            min={1}
                            max={120}
                            className={classNames({
                                'p-invalid': isFormSubmitted && !form.minutes,
                            })}
                        />
                    )}{' '}
                    <Button disabled={disabled} label="Submit" type="submit" />
                    {cancelMessageSubmission ? (
                        <Button disabled={disabled} label="Cancel" type="button" onClick={cancelMessageSubmission} />
                    ) : null}
                </div>
            </>,
        );
        return formData;
    };

    return (
        <div className="message-sender-container">
            <form onSubmit={handleSubmit}>
                {showForm ? (
                    renderForm()
                ) : (
                    <div className="new-conversation-btn">
                        <Button
                            disabled={disabled}
                            icon="pi pi-plus"
                            className="p-button p-component"
                            data-testid="new-row"
                            onClick={() => setShowForm(true)}
                        />
                    </div>
                )}
            </form>
        </div>
    );
};
